import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoDirective } from '@jsverse/transloco';
import { DateFilterComponent } from '../../../../../../../_shared/components';
import { EmptyStateComponent } from '../../../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../../../_shared/components/states/error-state/error-state.component';
import { DateInterval } from '../../../../../../../_shared/enums';
import { DateIntervalFilterData } from '../../../../../../../_shared/models';
import { ErrorHandlerService } from '../../../../../../../_shared/services/error-handler.service';
import { CurrencyHelper } from '../../../../../../../_shared/helpers/currency/currency-map.helper';
import { AuthService } from '../../../../../../../_shared/services';
import { AddStockComponent } from '../../add-stock/add-stock.component';
import { ReceivedPartHistoryItem, ReceivedPartHistoryRequest } from '../../../../models/received-part-history.model';
import { PartsAndInventoryService } from '../../../../services/parts-and-inventory.service';

@Component({
  selector: 'app-received-parts-tab',
  standalone: true,
  imports: [CommonModule, InfiniteScrollModule, DateFilterComponent, EmptyStateComponent, ErrorStateComponent, TranslocoDirective],
  templateUrl: './received-parts-tab.component.html',
  styleUrl: './received-parts-tab.component.scss'
})
export class ReceivedPartsTabComponent implements OnInit {
  @Input() partId!: number;
  @Input() accountIds: number[] = [];
  @Input() dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  @Output() stockAdded = new EventEmitter<void>();

  readonly defaultInterval = DateInterval.Month;
  readonly disabledIntervals: DateInterval[] = [DateInterval.Half];

  modalDateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  receivedHistory: ReceivedPartHistoryItem[] = [];
  isReceivedHistoryLoading = false;
  isLoadingMore = false;
  hasError = false;
  hasMoreData = true;

  private currentPage = 1;
  private totalPages = 0;
  private readonly pageSize = 20;
  private requestSequence = 0;

  private readonly modalService = inject(NgbModal);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly partsAndInventoryService = inject(PartsAndInventoryService);
  private readonly authService = inject(AuthService);

  ngOnInit(): void {
    this.modalDateFilter = this.cloneDateFilter(this.dateFilter);
    void this.resetAndLoadReceivedHistoryAsync();
  }

  get currencySymbol(): string {
    const currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR';
    return CurrencyHelper.getCurrencySymbol(currencyCode);
  }

  get shouldShowReceivedEmptyState(): boolean {
    return !this.isReceivedHistoryLoading && !this.hasError && this.receivedHistory.length === 0;
  }

  onDateFilterChange(filter: DateIntervalFilterData): void {
    this.modalDateFilter = { ...filter };
    void this.resetAndLoadReceivedHistoryAsync();
  }

  onScrollDown(): void {
    if (!this.hasMoreData || this.isLoadingMore || this.isReceivedHistoryLoading) {
      return;
    }

    void this.loadReceivedHistoryPageAsync(false);
  }

  openAddStock(): void {
    const modalRef = this.modalService.open(AddStockComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      windowClass: 'add-stock-modal-window'
    });

    modalRef.componentInstance.partId = this.partId;

    modalRef.result
      .then((result: boolean) => {
        if (result === true) {
          void this.resetAndLoadReceivedHistoryAsync();
          this.stockAdded.emit();
        }
      })
      .catch(() => { });
  }

  formatDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString('en-GB');
  }

  formatCost(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-';
    }

    return `${this.currencySymbol} ${value.toLocaleString('en-ZA')}`;
  }

  trackByHistory(index: number, item: ReceivedPartHistoryItem): string {
    return `${item.dateReceived}-${item.invoiceNumber ?? 'na'}-${item.receivedQty ?? 0}-${index}`;
  }

  private cloneDateFilter(filter: DateIntervalFilterData): DateIntervalFilterData {
    const hasRange = !!filter?.startDate && !!filter?.endDate;
    if (!hasRange) {
      const today = new Date();
      return {
        startDate: new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0),
        endDate: new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999),
        granularity: filter?.granularity ?? null,
        previousCycleStartDate: null,
        previousCycleEndDate: null,
        interval: filter?.interval ?? this.defaultInterval,
        isCustomDate: false
      };
    }

    return {
      startDate: new Date(filter.startDate!),
      endDate: new Date(filter.endDate!),
      granularity: filter?.granularity ?? null,
      previousCycleStartDate: filter?.previousCycleStartDate ? new Date(filter.previousCycleStartDate) : null,
      previousCycleEndDate: filter?.previousCycleEndDate ? new Date(filter.previousCycleEndDate) : null,
      interval: filter?.interval ?? this.defaultInterval,
      isCustomDate: filter?.isCustomDate ?? false
    };
  }

  private async resetAndLoadReceivedHistoryAsync(): Promise<void> {
    this.currentPage = 1;
    this.totalPages = 0;
    this.hasMoreData = true;
    this.hasError = false;
    this.receivedHistory = [];

    await this.loadReceivedHistoryPageAsync(true);
  }

  private async loadReceivedHistoryPageAsync(isInitialLoad: boolean): Promise<void> {
    const requestId = ++this.requestSequence;

    if (isInitialLoad) {
      this.isReceivedHistoryLoading = true;
      this.hasError = false;
    } else {
      this.isLoadingMore = true;
    }

    try {
      const request: ReceivedPartHistoryRequest = {
        partId: this.partId,
        accountIds: this.accountIds ?? [],
        dateFrom: this.toApiDate(this.modalDateFilter.startDate, false),
        dateTo: this.toApiDate(this.modalDateFilter.endDate, true),
        page: this.currentPage,
        pageSize: this.pageSize
      };

      const response = await this.partsAndInventoryService.getReceivedPartHistoryAsync(request);
      if (requestId !== this.requestSequence) {
        return;
      }

      const results = response.data?.results ?? [];
      this.receivedHistory = isInitialLoad ? results : [...this.receivedHistory, ...results];
      this.totalPages = response.data?.totalPages ?? 0;
      this.hasMoreData = this.currentPage < this.totalPages;

      if (this.hasMoreData) {
        this.currentPage += 1;
      }
    } catch (error: any) {
      if (requestId === this.requestSequence) {
        this.hasError = true;
        this.errorHandlerService.handleError(error);
      }
    } finally {
      if (requestId === this.requestSequence) {
        this.isReceivedHistoryLoading = false;
        this.isLoadingMore = false;
      }
    }
  }

  private toApiDate(date: Date | null, endOfDay: boolean): string {
    const value = date ? new Date(date) : new Date();

    if (endOfDay) {
      value.setHours(23, 59, 59, 0);
    } else {
      value.setHours(0, 0, 0, 0);
    }

    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    const seconds = String(value.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }
}